import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getCreditMemo, getCreditMemoItem, getCreditMemoStatuses, type CreditMemoItem, saveCreditMemo, type CreditMemo } from "../../api/erp";
import { MDBBtn, MDBCard, MDBCardBody, MDBInput, MDBInputGroup, MDBTable, MDBTableBody, MDBTableHead } from "mdb-react-ui-kit";
import useLocale, { I18N } from "../../util/i18n";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlusCircle } from "@fortawesome/free-solid-svg-icons";
import Select, { type OptionType } from "../../component/select";
import { format } from "date-fns";
import Toast from "../../component/toast";
import { useCrumbs } from "../../component/breadcrumbs";
import currencies from "../../util/currencies.json";
import BigNumber from "bignumber.js";
import Autocomplete from "../../component/autocomplete";

const DEFAULT_STATUSES = [
    { value: 'Placed', label: 'Placed' },
    { value: 'Approved', label: 'Approved' },
    { value: 'Declined', label: 'Declined' },
    { value: 'Deleted', label: 'Deleted' }
];

export default function CreditMemo(props: { editable: boolean }) {
    let { 0: query } = useSearchParams();
    let { 0: order, 1: setOrder } = React.useState<CreditMemo>({ id: '', credit_id: '', created_at: 0, subtotal: 0, tax: 0, total: 0, currency: 'USD', status: 'Placed' });
    let { 0: items, 1: setItems } = React.useState<Array<CreditMemoItem>>([]);
    let { 0: statuses, 1: setStatuses } = React.useState<Array<OptionType>>(DEFAULT_STATUSES);
    let { 0: __, 1: lang } = useLocale();
    let formatter = React.useMemo(() => new Intl.NumberFormat(lang, { style: 'currency', currency: order.currency || 'USD' }), [order, lang]);
    let { 0: setCrumbs } = useCrumbs();
    let navigate = useNavigate();
    let save = React.useCallback((e: React.FormEvent<HTMLFormElement>) => {
        e.stopPropagation();
        e.preventDefault();
        if (!(e.target as HTMLFormElement).checkValidity()) {
            return;
        }
        let subtotal = new BigNumber(0), tax = new BigNumber(0), total = new BigNumber(0);
        for (let i in items) {
            if (!parseFloat(items[i].rowtotal?.toString())) {
                items[i].rowtotal = items[i].credit_price;
            }
            subtotal = subtotal.plus(items[i].rowtotal);
            tax = tax.plus(items[i].tax);
            total = total.plus(items[i].rowtotal).plus(items[i].tax);
        }
        if (!parseFloat(order.subtotal.toString())) {
            order.subtotal = subtotal.toNumber();
        }
        if (!parseFloat(order.tax.toString())) {
            order.tax = tax.toNumber();
        }
        if (!parseFloat(order.total.toString())) {
            order.total = total.toNumber();
        }
        saveCreditMemo({ ...order, item: items }).then(response => {
            if (typeof response !== 'string' && response.data) {
                let statuses: Array<OptionType> = JSON.parse(window.sessionStorage.getItem('CM-status') ?? '[]');
                if (statuses.filter(p => p.value === order.status).length === 0) {
                    statuses.push({ value: order.status, label: order.status });
                    setStatuses(statuses);
                }
                navigate('/credit-memo/edit?id=' + response.data, { replace: true });
            }
            Toast.show('Data has been saved.');
        }, () => Toast.show(__('An error detected. Please try again later.')));
    }, [order, items, __]);
    let onChangeOrder = React.useCallback((e: { target: { name: string, value: string } } | string) => {
        setOrder(old => {
            if (typeof e === 'string') {
                return { ...old, status: e };
            } else if (e.target.name.endsWith('_at')) {
                return { ...old, [e.target.name]: Date.parse(e.target.value) / 1000 };
            } else {
                return { ...old, [e.target.name]: e.target.value };
            }
        });
    }, [setOrder]);
    let onChangeItem = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setItems(old => {
            const { 0: key, 1: index } = e.target.name.substring(0, e.target.name.length - 1).split('[');
            let cloned = [...old];
            //@ts-ignore
            cloned[parseInt(index)][key] = e.target.value;
            return cloned;
        });
    }, [setItems]);
    let add = React.useCallback((e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
        e.stopPropagation();
        e.preventDefault();
        setItems(old => ([...old, { id: '', sku: '', qty: 0, credit_price: 0, tax: 0, rowtotal: 0, comments: '', credit_id: order.id }]));
    }, [setItems, order]);
    React.useEffect(() => {
        let id = query.get('id');
        if (id) {
            let cached = window.sessionStorage.getItem('CM-' + id);
            if (cached) {
                let order = JSON.parse(cached);
                setOrder(order);
                setCrumbs([{ label: 'Order', link: '/credit-memo' }, order.credit_id]);
            } else {
                getCreditMemo({ filter: { id }, limit: 1 }).then(response => {
                    if (typeof response !== 'string' && response.data.length) {
                        setCrumbs([{ label: 'Order', link: '/credit-memo' }, response.data[0].credit_id]);
                        setOrder(response.data[0]);
                    }
                });
            }
            getCreditMemoItem({ filter: { order_id: id }, limit: -1 }).then(response => {
                if (typeof response !== 'string') {
                    setItems(response.data);
                }
            });
        } else {
            setCrumbs([{ label: 'Order', link: '/credit-memo' }, 'Edit']);
        }
        let statuses = window.sessionStorage.getItem('CM-status');
        if (statuses) {
            setStatuses(JSON.parse(statuses));
        } else {
            getCreditMemoStatuses().then(response => {
                let result = response.map(item => ({ value: item.status, label: item.status + '(' + item.count + ')' }));
                window.sessionStorage.setItem('CM-status', JSON.stringify(result));
                setStatuses(result);
            });
        }
    }, [query, setOrder, setItems, props.editable]);
    return (
        <form className="edit h-100" onSubmit={save}>
            <div className="accordion">
                <MDBCard className="mb-3">
                    <MDBCardBody>
                        <input type="hidden" name="id" value={order.id} />
                        <MDBInput label={__('Credit Memo ID')} required readOnly={!props.editable} wrapperClass="mb-3" className="active" name="credit_id" value={order.credit_id} onChange={onChangeOrder} />
                        <Select label={__('Currency')} required readOnly={!props.editable} options={currencies.map(value => ({ value, label: __(value) }))} value={order.currency || 'USD'} onChange={onChangeOrder} wrapperClass="mb-3"></Select>
                        <MDBInput type="number" label={__('Subtotal')} min="0" step="0.0001" readOnly={!props.editable} wrapperClass="mb-3" className="active" name="subtotal" value={order.subtotal} onChange={onChangeOrder} />
                        <MDBInput type="number" label={__('Tax')} min="0" step="0.0001" readOnly={!props.editable} wrapperClass="mb-3" className="active" name="tax" value={order.tax} onChange={onChangeOrder} />
                        <MDBInput type="number" label={__('Grand Total')} min="0" step="0.0001" readOnly={!props.editable} wrapperClass="mb-3" className="active" name="total" value={order.total} onChange={onChangeOrder} />
                        <MDBInput type="date" label={__('Created at')} required readOnly={!props.editable} wrapperClass="mb-3" className="active" name="created_at" value={format((order.created_at || Date.now() / 1000) * 1000, 'yyyy-MM-dd')} onChange={onChangeOrder} />
                        <Autocomplete label={__('Status')} required readOnly={!props.editable} name="status" value={order.status} options={statuses?.length ? statuses : DEFAULT_STATUSES} onChange={onChangeOrder} />
                    </MDBCardBody>
                </MDBCard>
                <MDBCard>
                    <MDBCardBody>
                        <MDBTable>
                            <MDBTableHead>
                                <tr>
                                    <th>SKU</th>
                                    <th className="text-nowrap text-center">{__('Qty')}</th>
                                    <th className="text-nowrap text-center">{__('Credit Price')}</th>
                                    <th className="text-nowrap text-center">{__('Tax')}</th>
                                    <th className="text-nowrap text-center">{__('Row Total')}</th>
                                    <th className="text-nowrap text-center">{__('Reason')}</th>
                                    <th className="text-nowrap text-center">{__('Comments')}</th>
                                </tr>
                            </MDBTableHead>
                            {props.editable ? (
                                <>
                                    <MDBTableBody>
                                        {items.map((item, i) => (
                                            <tr key={item.id + i.toString()}>
                                                <th>
                                                    <input type="hidden" name={'id[' + i + ']'} value={item.id} />
                                                    <MDBInput name={'sku[' + i + ']'} value={item.sku} onChange={onChangeItem} />
                                                </th>
                                                <td><MDBInput type="number" min="0" step="0.0001" name={'qty[' + i + ']'} value={item.qty} onChange={onChangeItem} /></td>
                                                <td>
                                                    <MDBInputGroup textBefore={order.currency}>
                                                        <MDBInput type="number" min="0" step="0.0001" name={'credit_price[' + i + ']'} value={item.credit_price} onChange={onChangeItem} />
                                                    </MDBInputGroup>
                                                </td>
                                                <td>
                                                    <MDBInputGroup textBefore={order.currency}>
                                                        <MDBInput type="number" min="0" step="0.0001" name={'tax[' + i + ']'} value={item.tax} onChange={onChangeItem} />
                                                    </MDBInputGroup>
                                                </td>
                                                <td>
                                                    <MDBInputGroup textBefore={order.currency}>
                                                        <MDBInput type="number" min="0" step="0.0001" name={'rowtotal[' + i + ']'} value={item.rowtotal} onChange={onChangeItem} />
                                                    </MDBInputGroup>
                                                </td>
                                                <td><MDBInput name={'reason[' + i + ']'} value={item.reason ?? ''} onChange={onChangeItem} /></td>
                                                <td><MDBInput name={'comments[' + i + ']'} value={item.comments ?? ''} onChange={onChangeItem} /></td>
                                            </tr>
                                        ))}
                                    </MDBTableBody>
                                    <tfoot>
                                        <tr>
                                            <td colSpan={7}>
                                                <MDBBtn color="light" block onClick={add}>
                                                    <FontAwesomeIcon icon={faPlusCircle} size="2x" />
                                                </MDBBtn>
                                            </td>
                                        </tr>
                                    </tfoot>
                                </>
                            ) : (
                                <MDBTableBody>
                                    {items.map(item => (
                                        <tr key={item.id}>
                                            <th>{item.sku}</th>
                                            <td className="text-center">{parseFloat(item.qty.toString())}</td>
                                            <td className="text-center">{formatter.format(item.credit_price || 0)}</td>
                                            <td className="text-center">{formatter.format(item.tax || 0)}</td>
                                            <td className="text-center">{formatter.format(item.rowtotal || 0)}</td>
                                            <td className="text-center">{item.reason}</td>
                                            <td className="text-center">{item.comments}</td>
                                        </tr>
                                    ))}
                                </MDBTableBody>
                            )}
                        </MDBTable>
                    </MDBCardBody>
                </MDBCard>
            </div>
            {props.editable ? (
                <MDBCard className="tools">
                    <MDBCardBody>
                        <div className="btns">
                            <MDBBtn color="info" type="reset"><I18N>Reset</I18N></MDBBtn>
                            <MDBBtn color="primary" type="submit"><I18N>Save</I18N></MDBBtn>
                        </div>
                    </MDBCardBody>
                </MDBCard>
            ) : null}
        </form>
    );
}